import { AppRole } from '@craft/database';

import {
  type AuthenticatedUser,
  type AuthenticatedUserWithSelectedRole,
  type ClientAuthenticatedUser,
  type ClientAuthenticatedUserWithSelectedRole,
  type ClientOrganizationRole,
  type DistributedClientOrganizationRole,
  type DistributedOrganizationRole,
  type OrganizationRole,
} from 'connect-graphql/auth';
import {
  type EvaluatingAppRole,
  EvaluatingAppRoles,
  type ToGraphQlAppRole,
} from 'connect-graphql/auth/roles';
import { type AppRole as GraphQlAppRole } from 'connect-graphql/generated';

import { AccessTypeId, hasAccessType } from '../accessTypes';
import { type InferGraphQlAppRole, type InferPrismaAppRole, type Roles } from '../types';

import * as types from './types';

/**
 * A factory function that generates an overloaded typeguard function, {@link types.RoleAuthorizer},
 * for a given {@link AppRole} and its corresponding client-side GraphQL representation,
 * {@link GraphQlAppRole}, based on a provided typeguard for the role itself.
 *
 * @param {types.RoleAuthorizerTypeGuard<R, G>} typeguard
 *   A typeguard that returns whether or not the provided role, {@link AppRole} or
 *   {@link GraphQlAppRole}, is the role for which the resulting {@link types.RoleAuthorizer} is
 *   being generated.
 *
 * @returns {types.RoleAuthorizer<R, G>}
 */
function roleAuthorizer<R extends AppRole, G extends GraphQlAppRole>(
  typeguard: types.RoleAuthorizerTypeGuard<R, G>,
): types.RoleAuthorizer<R, G> {
  function isRole(role: GraphQlAppRole | undefined): role is G;
  function isRole(role: AppRole | undefined): role is R;
  function isRole(user: AuthenticatedUser): user is AuthenticatedUserWithSelectedRole<R>;
  function isRole(
    user: ClientAuthenticatedUser,
  ): user is ClientAuthenticatedUserWithSelectedRole<G>;
  function isRole(orgRole: DistributedOrganizationRole): orgRole is DistributedOrganizationRole<R>;
  function isRole(orgRole: OrganizationRole): orgRole is OrganizationRole<R>;
  function isRole(orgRole: ClientOrganizationRole): orgRole is ClientOrganizationRole<G>;
  function isRole(
    orgRole: DistributedClientOrganizationRole,
  ): orgRole is DistributedClientOrganizationRole<G>;

  /**
   * The implementation signature of the overloaded function, which takes the union of all of the
   * possible argument types for the overloads.
   */
  function isRole(arg0: types.RoleAuthorizerPrimaryArg): boolean {
    if (types.isArgAuthenticatedUser(arg0)) {
      return isRole(arg0.selectedOrganizationRole?.role);
    } else if (types.isArgOrganizationRole(arg0)) {
      return isRole(arg0.role);
    }
    return arg0 !== undefined && typeguard(arg0);
  }

  return isRole;
}

/**
 * A typeguard that returns whether or not the provided role, {@link AppRole}, organization role,
 * {@link DistributedOrganizationRole}, or authenticated user, {@link AuthenticatedUser}, is
 * associated with the {@link AccessTypeId.Learner} access type, for both client-side GraphQL
 * and server-side Prisma representsations of the {@link AppRole} and {@link OrganizationType}.
 *
 * @see {@link RoleAuthorizer}
 */
export const isLearner = roleAuthorizer<typeof AppRole.LEARNER, GraphQlAppRole.Learner>(
  (role): role is GraphQlAppRole.Learner | typeof AppRole.LEARNER =>
    hasAccessType(AccessTypeId.Learner, { role }),
);

/**
 * A typeguard that returns whether or not the provided role, {@link AppRole}, organization role,
 * {@link DistributedOrganizationRole}, or authenticated user, {@link AuthenticatedUser}, is
 * associated with the {@link AccessTypeId.Evaluator} access type, for both client-side GraphQL
 * and server-side Prisma representsations of the {@link AppRole} and {@link OrganizationType}.
 *
 * @see {@link RoleAuthorizer}
 */
export const isEvaluator = roleAuthorizer<typeof AppRole.EVALUATOR, GraphQlAppRole.Evaluator>(
  (role): role is GraphQlAppRole.Evaluator | typeof AppRole.EVALUATOR =>
    hasAccessType(AccessTypeId.Evaluator, { role }),
);

/**
 * A typeguard that returns whether or not the provided role, {@link AppRole}, organization role,
 * {@link DistributedOrganizationRole}, or authenticated user, {@link AuthenticatedUser}, is
 * associated with the {@link AccessTypeId.Facilitator} access type, for both client-side GraphQL
 * and server-side Prisma representsations of the {@link AppRole} and {@link OrganizationType}.
 *
 * @see {@link RoleAuthorizer}
 */
export const isFacilitator = roleAuthorizer<typeof AppRole.FACILITATOR, GraphQlAppRole.Facilitator>(
  (role): role is GraphQlAppRole.Facilitator | typeof AppRole.FACILITATOR =>
    hasAccessType(AccessTypeId.Facilitator, { role }),
);

/**
 * A typeguard that returns whether or not the provided role, {@link AppRole}, organization role,
 * {@link DistributedOrganizationRole}, or authenticated user, {@link AuthenticatedUser}, is
 * associated with the {@link AccessTypeId.Instructor} access type, for both client-side GraphQL
 * and server-side Prisma representsations of the {@link AppRole} and {@link OrganizationType}.
 *
 * @see {@link RoleAuthorizer}
 */
export const isInstructor = roleAuthorizer<typeof AppRole.INSTRUCTOR, GraphQlAppRole.Instructor>(
  (role): role is GraphQlAppRole.Instructor | typeof AppRole.INSTRUCTOR =>
    hasAccessType(AccessTypeId.Instructor, { role }),
);

/**
 * A typeguard that returns whether or not the provided role, {@link AppRole}, organization role,
 * {@link DistributedOrganizationRole}, or authenticated user, {@link AuthenticatedUser}, is
 * associated with the {@link AccessTypeId.Observer} access type, for both client-side GraphQL
 * and server-side Prisma representsations of the {@link AppRole} and {@link OrganizationType}.
 *
 * @see {@link RoleAuthorizer}
 */
export const isObserver = roleAuthorizer<typeof AppRole.OBSERVER, GraphQlAppRole.Observer>(
  (role): role is GraphQlAppRole.Observer | typeof AppRole.OBSERVER =>
    hasAccessType(AccessTypeId.Observer, { role }),
);

/**
 * A typeguard that returns whether or not the provided role, {@link AppRole}, organization role,
 * {@link DistributedOrganizationRole}, or authenticated user, {@link AuthenticatedUser}, is
 * associated with the {@link AccessTypeId.Admin} access type, for both client-side GraphQL
 * and server-side Prisma representsations of the {@link AppRole} and {@link OrganizationType}.
 *
 * @see {@link RoleAuthorizer}
 */
export const isAdmin = roleAuthorizer<typeof AppRole.ADMIN, GraphQlAppRole.Admin>(
  (role): role is GraphQlAppRole.Admin | typeof AppRole.ADMIN =>
    hasAccessType(AccessTypeId.Admin, { role }),
);

/**
 * A type that represents the implementation type of the typeguard {@link hasAnyRole}, without
 * regard to it's overloaded signatures. This is used to safely coerce the overloaded method type
 * to a single function type that can be called within the implementation of
 * {@link assertHasAnyRole} without a type coercion at the call site.
 *
 * This should never be used outside of this file.
 *
 * This coercion is safe because the implementation signature of {@link hasAnyRole} is guaranteed
 * to be compatible with all of its overload signatures.
 */
export type ServerClientAgnosticHasAnyRoleTypeguard<R extends Roles> = (
  arg0: types.RoleAuthorizerPrimaryArg,
  roles: R,
) => boolean;

const RoleAuthorizerMap = {
  [AppRole.ADMIN]: isAdmin,
  [AppRole.EVALUATOR]: isEvaluator,
  [AppRole.FACILITATOR]: isFacilitator,
  [AppRole.INSTRUCTOR]: isInstructor,
  [AppRole.LEARNER]: isLearner,
  [AppRole.OBSERVER]: isObserver,
} as const satisfies Record<AppRole, types.RoleAuthorizer>;

export function hasAnyRole<Rs extends Roles<G>, G extends GraphQlAppRole>(
  role: GraphQlAppRole | undefined,
  roles: Rs,
): role is InferGraphQlAppRole<Rs>;

export function hasAnyRole<Rs extends Roles<R>, R extends AppRole>(
  role: AppRole | undefined,
  roles: Rs,
): role is InferPrismaAppRole<Rs>;

export function hasAnyRole<Rs extends Roles<R>, R extends AppRole>(
  user: AuthenticatedUser,
  roles: Rs,
): user is AuthenticatedUserWithSelectedRole<InferPrismaAppRole<Rs>>;

export function hasAnyRole<Rs extends Roles<R>, R extends GraphQlAppRole>(
  user: ClientAuthenticatedUser,
  roles: Rs,
): user is ClientAuthenticatedUserWithSelectedRole<InferGraphQlAppRole<Rs>>;

export function hasAnyRole<Rs extends Roles<R>, R extends AppRole>(
  orgRole: DistributedOrganizationRole,
  roles: Rs,
): orgRole is DistributedOrganizationRole<InferPrismaAppRole<Rs>>;

export function hasAnyRole<Rs extends Roles<R>, R extends GraphQlAppRole>(
  orgRole: DistributedClientOrganizationRole,
  roles: Rs,
): orgRole is DistributedClientOrganizationRole<InferGraphQlAppRole<Rs>>;

export function hasAnyRole<Rs extends Roles<R>, R extends AppRole>(
  orgRole: OrganizationRole,
  roles: Rs,
): orgRole is OrganizationRole<InferPrismaAppRole<Rs>>;

export function hasAnyRole<Rs extends Roles<R>, R extends GraphQlAppRole>(
  orgRole: ClientOrganizationRole,
  roles: Rs,
): orgRole is ClientOrganizationRole<InferGraphQlAppRole<Rs>>;

/**
 * Returns whether or not the provided role, {@link AppRole} or {@link GraphQlAppRole}, organization
 * role, {@link DistributedOrganizationRole} or {@link OrganizationRole}, or authenticated user,
 * {@link AuthenticatedUser}, is associated with any of the provided roles.
 *
 * @example
 * ```ts
 * const user: AuthenticatedUser = { ... };
 * if (hasAnyRole(user, [AppRole.ADMIN, AppRole.INSTRUCTOR])) {
 *   const role = user.selectedOrganizationRole.role; // Typed as 'ADMIN' | 'INSTRUCTOR'
 * }
 * ```
 */
export function hasAnyRole<
  R extends AppRole[] | readonly AppRole[],
  G extends GraphQlAppRole[] | readonly GraphQlAppRole[],
>(arg0: types.RoleAuthorizerPrimaryArg, roles: G | R) {
  if (roles.length === 0) {
    throw new Error("At least one role must be provided to 'hasAnyRole'.");
  }
  return roles.some(role => {
    /* This type coercion is safe - see ClientServerAgnosticRoleAuthorizer. */
    const typeguard = RoleAuthorizerMap[role] as types.ClientServerAgnosticRoleAuthorizer;
    /* Even though it appears redundant, typeguarding of the members of the union represented by
       RoleAuthorizerPrimaryArg is necessary to properly narrow the type for the typeguard function
       and call the method without a type coercion. */
    if (types.isArgAuthenticatedUser(arg0)) {
      return typeguard(arg0);
    } else if (types.isArgOrganizationRole(arg0)) {
      return typeguard(arg0);
    }
    return typeguard(arg0);
  });
}

export function hasEvaluatingAccess(
  role: GraphQlAppRole | undefined,
): role is Extract<GraphQlAppRole, EvaluatingAppRole>;

export function hasEvaluatingAccess(role: AppRole | undefined): role is EvaluatingAppRole;

export function hasEvaluatingAccess(
  user: AuthenticatedUser,
): user is AuthenticatedUserWithSelectedRole<EvaluatingAppRole>;

export function hasEvaluatingAccess(
  orgRole: DistributedOrganizationRole,
): orgRole is DistributedOrganizationRole<EvaluatingAppRole>;

export function hasEvaluatingAccess(
  orgRole: OrganizationRole,
): orgRole is { readonly role: EvaluatingAppRole } & OrganizationRole;

/**
 * Determines whether or not the provided role, {@link AppRole}, organization role,
 * {@link DistributedOrganizationRole}, or authenticated user, {@link AuthenticatedUser}, is
 * associated with an evaluating role, {@link EvaluatingAppRole}.
 *
 * @param {types.RoleAuthorizerPrimaryArg} arg0
 *   The argument for which the determination is being made as to whether or not it is associated
 *   with a role that is considered an evaluating role, {@link EvaluatingAppRole}.
 *
 *   @see {@link types.RoleAuthorizerPrimaryArg}
 *
 * @returns {boolean}
 *   A boolean value indicating whether or not the provided role, organization role, or
 *   authenticated user is associated with an evaluating role, {@link EvaluatingAppRole}.
 */
export function hasEvaluatingAccess(arg0: types.RoleAuthorizerPrimaryArg): boolean {
  const safelyCoercedHasAnyRole = hasAnyRole as ServerClientAgnosticHasAnyRoleTypeguard<
    Roles<EvaluatingAppRole | ToGraphQlAppRole<EvaluatingAppRole>>
  >;
  return safelyCoercedHasAnyRole(arg0, EvaluatingAppRoles);
}
